import {
  getInsurancePlan,
  InsuranceCoverage,
  // InsurancePlans,
  isInsuranceCoverageRequest,
  PatientCoverageId,
  PopulatedInsurancePlans,
  PopulatedPregnancyPlans,
} from '@hb/shared'
import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useProfile } from '../../../contexts/user/ProfileContext'

interface TemplateInsurancePlansContextData {
  pregnancyPlans: PopulatedPregnancyPlans | undefined
  insurancePlans: PopulatedInsurancePlans | undefined
  selectPlan: (planId: PatientCoverageId) => void
  selectedPlan: InsuranceCoverage | null
  selectedPlanId: PatientCoverageId
}

export const InsurancePlansTemplateContext = createContext<TemplateInsurancePlansContextData>({
  insurancePlans: { primary: null, secondary: null, additional: {}, requests: {} },
  selectedPlan: null,
  selectPlan: () => {},
  selectedPlanId: 'primary',
  pregnancyPlans: undefined,
})

const useTemplateInsurancePlansData = (): TemplateInsurancePlansContextData => {
  const {
    user,
    selectedAssessment: { populated: assessment },
    assessmentId,
  } = useProfile()

  const { insurancePlans } = user ?? {}
  const { plans: pregnancyPlans } = assessment ?? {}

  const [selectedPlanId, setPlanId] = useState<PatientCoverageId>(
    assessment?.primaryCoverageId ?? 'primary',
  )

  const prevAssessmentId = useRef<string | null>(null)
  useEffect(() => {
    if (assessment && assessmentId !== prevAssessmentId.current) {
      setPlanId(assessment.primaryCoverageId ?? 'primary')
      prevAssessmentId.current = assessmentId
    }
  }, [assessment, assessmentId])

  const selectedPlan = useMemo(() => {
    if (!insurancePlans) return null
    const plan = getInsurancePlan(insurancePlans, selectedPlanId)
    if (!plan || isInsuranceCoverageRequest(plan)) return null
    return plan
  }, [insurancePlans, selectedPlanId])

  return useMemo<TemplateInsurancePlansContextData>(
    () => ({
      insurancePlans,
      pregnancyPlans,
      selectPlan: (id: PatientCoverageId) => {
        setPlanId(id)
      },
      selectedPlanId,
      selectedPlan,
    }),
    [insurancePlans, selectedPlanId, selectedPlan, pregnancyPlans],
  )
}

export const TemplateInsurancePlansProvider = ({ children }: PropsWithChildren) => {
  const contextData = useTemplateInsurancePlansData()
  return (
    <InsurancePlansTemplateContext.Provider value={contextData}>
      {children}
    </InsurancePlansTemplateContext.Provider>
  )
}

export const useTemplateInsurancePlans = () => useContext(InsurancePlansTemplateContext)
